import React from 'react';
import PublicIcon from '@material-ui/icons/Public';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import WorkIcon from '@material-ui/icons/Work';
import CategoryIcon from '@material-ui/icons/Category';
import AccountTreeIcon from '@material-ui/icons/AccountTree';

export interface DimensionConfig {
  key: string;
  title: string;
  /** Title shown when the viewer is a partner/lead (e.g. "Team Members" instead of "Matter Leads") */
  partnerTitle?: string;
  /** Tooltip explaining this dimension. {name} is replaced with the user's name. */
  description: (name: string) => string;
  /** Tooltip for the partner view (when isPartner is true) */
  partnerDescription?: (name: string) => string;
  color: string;
  chipBg: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  /** How many items to show before "Show all/more" */
  defaultVisible: number;
  /** Max items when expanded (undefined = show all) */
  maxExpanded?: number;
  /** Show country flags next to labels */
  showFlag?: boolean;
  /** Show profile picture avatars next to labels */
  showAvatar?: boolean;
  /** Use co-staffing days as primary metric instead of hours */
  useCoStaffingDays?: boolean;
}

export const DIMENSION_CONFIG: Record<string, DimensionConfig> = {
  country: {
    key: 'country',
    title: 'Client Countries',
    description: (name) => `Countries where ${name} has worked for clients, based on client registration.`,
    color: '#4A90D9',
    chipBg: '#E8F0FA',
    icon: PublicIcon,
    defaultVisible: 5,
    showFlag: true,
  },
  partner: {
    key: 'partner',
    title: 'Responsible Partners',
    partnerTitle: 'Responsible For',
    description: (name) => `Partners listed as responsible on matters ${name} has worked on. Hours shown are ${name}'s hours on those matters.`,
    partnerDescription: (name) => `People who have logged hours on matters where ${name} is listed as responsible.`,
    color: '#E74C8B',
    chipBg: '#FDEEF4',
    icon: SupervisorAccountIcon,
    defaultVisible: 5,
    showAvatar: true,
  },
  client: {
    key: 'client',
    title: 'Top Clients',
    description: (name) => `Clients ${name} has logged the most hours for.`,
    color: '#6B8E6B',
    chipBg: '#EDF3ED',
    icon: BusinessIcon,
    defaultVisible: 5,
    maxExpanded: 20,
  },
  teamMember: {
    key: 'teamMember',
    title: 'Responsible For',
    description: (name) => `People who have logged hours on matters where ${name} is listed as responsible.`,
    color: '#E74C8B',
    chipBg: '#FDEEF4',
    icon: SupervisorAccountIcon,
    defaultVisible: 5,
    showAvatar: true,
  },
  peer: {
    key: 'peer',
    title: 'Works Frequently With',
    description: (name) => `People who have logged time on the same matter on the same day as ${name} — an approximation of how closely they have worked together.`,
    color: '#E8913F',
    chipBg: '#FEF3E8',
    icon: PeopleIcon,
    defaultVisible: 5,
    showAvatar: true,
    useCoStaffingDays: true,
  },
  workType: {
    key: 'workType',
    title: 'Work Types',
    description: (name) => `Types of legal work ${name} has been involved in, based on matter classification.`,
    color: '#7B68EE',
    chipBg: '#EEEAFD',
    icon: WorkIcon,
    defaultVisible: 6,
  },
  industry: {
    key: 'industry',
    title: 'Industries',
    description: (name) => `Industry sectors ${name} has worked in, based on client or matter classification.`,
    color: '#2EAD7F',
    chipBg: '#E6F7F0',
    icon: CategoryIcon,
    defaultVisible: 6,
  },
  practiceArea: {
    key: 'practiceArea',
    title: 'Practice Areas',
    description: (name) => `Practice areas ${name} has worked in, based on matter classification.`,
    color: '#8B6DAF',
    chipBg: '#F0EBF5',
    icon: AccountTreeIcon,
    defaultVisible: 6,
  },
};

export const DIMENSION_ORDER = ['country', 'partner', 'teamMember', 'client', 'peer', 'workType', 'industry', 'practiceArea'];
